import { useState } from 'react'
import ModalForm from '@/gf/components/ModalForm'
import TextInput from '@/gf/components/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import useGqlClient from '../../hooks/useGqlClient'
import { useEditWorkOrderMutation } from '../../_gen/gql'
import WorkOrderCombobox from '../CreateRequest/WorkOrderCombobox'
import useSession from '@/buyers/hooks/useSession'
import FleetioWorkOrderNumber from '@/buyers/pages/CreateRequest/FleetioWorkOrderNumber'
import ClueWorkOrderNumber from '@/buyers/pages/CreateRequest/ClueWorkOrderNumber'

const EditWorkOrderModal = ({
  open,
  onClose,
  woNumber,
  unitNumber,
  requestId,
  refetch,
  orgMachine,
  fleetioWorkOrderId,
  externalWorkOrderId,
}: {
  open: boolean
  onClose: () => void
  woNumber: string | null
  requestId: string
  unitNumber: string | undefined
  refetch: () => unknown
  orgMachine: { id: string; fleetioId: number | null } | null
  fleetioWorkOrderId: number | null
  externalWorkOrderId: string | null
}) => {
  const [_, messages] = useMsgs()
  const { featureFlags, features } = useSession()
  const [workOrderNumber, setWorkOrderNumber] = useState(woNumber || null)
  const [editWorkOrder] = useEditWorkOrderMutation({ client: useGqlClient() })

  const [getfleetioWorkOrderId, setFleetioWorkOrderId] = useState(fleetioWorkOrderId)
  const [getExternalWorkOrderId, setExternalWorkOrderId] = useState(externalWorkOrderId)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!workOrderNumber && !getfleetioWorkOrderId && !getExternalWorkOrderId) {
      messages.add('Please enter a work order number', 'negative')
      return
    }
    editWorkOrder({
      variables: {
        id: requestId,
        workOrderNumber,
        fleetioWorkOrderId: getfleetioWorkOrderId,
        externalWorkOrderId: getExternalWorkOrderId,
      },
    })
      .then(() => {
        refetch()
        messages.add('Work Order Number Updated!', 'positive')
      })
      .catch(() => {
        messages.add('Error updating work order number', 'negative')
      })
      .finally(onClose)
  }

  return (
    <ModalForm
      title="Edit Work Order Number"
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      submitButtonText="Save"
    >
      <div className="flex flex-col gap-y-2">
        {features.hasClueIntegration && orgMachine?.id ? (
          <ClueWorkOrderNumber
            workOrderId={getExternalWorkOrderId ?? undefined}
            workOrderNumber={workOrderNumber}
            orgMachineId={orgMachine.id}
            onChange={({ id, number }) => {
              // The Fleetio id is mutually exclusive with the Clue id, so it
              // must be cleared. Keep the human WO number alongside the Clue id
              // so the RFQ details row renders the number mechanics recognise.
              setExternalWorkOrderId(id)
              setWorkOrderNumber(number)
              setFleetioWorkOrderId(null)
            }}
          />
        ) : orgMachine?.id && orgMachine.fleetioId ? (
          <FleetioWorkOrderNumber
            workOrderId={getfleetioWorkOrderId ?? undefined}
            orgMachineId={orgMachine?.id}
            onChange={(value) => {
              setFleetioWorkOrderId(value)
              setWorkOrderNumber(null)
              setExternalWorkOrderId(null)
            }}
          />
        ) : featureFlags.assetworksApiIssuePartsToWorkOrder ? (
          <WorkOrderCombobox
            workOrder={workOrderNumber ?? undefined}
            onWorkOrderChange={(value) => {
              setWorkOrderNumber(value)
              setFleetioWorkOrderId(null)
              setExternalWorkOrderId(null)
            }}
            unitNumber={unitNumber}
          />
        ) : (
          <TextInput
            value={workOrderNumber}
            setValue={(value) => {
              setWorkOrderNumber(value)
              setFleetioWorkOrderId(null)
              setExternalWorkOrderId(null)
            }}
          />
        )}
      </div>
    </ModalForm>
  )
}

export default EditWorkOrderModal
